import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";

type SearchOperator = "contains" | "equals" | "starts_with" | "ends_with";
type SearchConjunction = "and" | "or";

interface SearchFieldDescriptor {
  key: string;
  label: string;
  data_type: "text" | "number" | "date";
  supports_quick: boolean;
}

interface SearchEntityDescriptor {
  key: string;
  label: string;
  description?: string | null;
  default_quick_fields: string[];
  fields: SearchFieldDescriptor[];
}

interface QuickSearchPayload {
  query: string;
  field?: string | null;
  operator?: SearchOperator;
}

interface AdvancedCriterionInput {
  id: string;
  field: string;
  operator: SearchOperator;
  value: string;
  conjunction: SearchConjunction;
}

interface SearchItem {
  id: string | number;
  title: string;
  subtitle?: string | null;
  entity: string;
  payload: Record<string, unknown>;
}

interface SearchResponse {
  entity: string;
  total_count: number;
  page: number;
  page_size: number;
  items: SearchItem[];
}

interface SavedSearchSummary {
  id: number;
  name: string;
  entity: string;
  description?: string | null;
  is_global: boolean;
}

interface SavedSearchDetail extends SavedSearchSummary {
  quick?: QuickSearchPayload | null;
  criteria: AdvancedCriterionInput[];
}

interface ButtonSlotConfig {
  slot: number;
  saved_search_id: number;
  label?: string | null;
}

interface ButtonConfigResponse {
  slots: ButtonSlotConfig[];
}

const ROOT_PATH = (window as any).ROOT_PATH ? String((window as any).ROOT_PATH).replace(/\/$/, "") : "";

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${ROOT_PATH}${path}`, {
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    ...init,
  });
  if (!response.ok) {
    const detail = await response.text();
    throw new Error(detail || "API call failed");
  }
  if (response.status === 204) {
    return undefined as T;
  }
  const text = await response.text();
  if (!text) {
    return undefined as T;
  }
  try {
    return JSON.parse(text) as T;
  } catch (_err) {
    return text as unknown as T;
  }
}

function uid(): string {
  return Math.random().toString(36).slice(2);
}

const operatorOptions: { value: SearchOperator; label: string }[] = [
  { value: "contains", label: "enthält" },
  { value: "equals", label: "ist gleich" },
  { value: "starts_with", label: "beginnt mit" },
  { value: "ends_with", label: "endet mit" },
];

const conjunctionOptions: { value: SearchConjunction; label: string }[] = [
  { value: "and", label: "UND" },
  { value: "or", label: "ODER" },
];

const MAX_BUTTON_SLOTS = 6;

const defaultAdvancedRow = (field: string): AdvancedCriterionInput => ({
  id: uid(),
  field,
  operator: "contains",
  value: "",
  conjunction: "and",
});

const buildResultLink = (item: SearchItem): string | null => {
  const baseCustomers = `${ROOT_PATH}/admin/customers`;
  const userId = (item.payload?.["user_id"] as number | string | undefined) ?? (item.payload as any)?.userId;
  switch (item.entity) {
    case "customer":
      return `${baseCustomers}/${item.id}`;
    case "contact":
      return userId ? `${baseCustomers}/${userId}?tab=kontakte` : baseCustomers;
    case "inquiry":
      return userId ? `${baseCustomers}/${userId}?tab=anfragen` : baseCustomers;
    case "time_entry":
      return userId ? `${baseCustomers}/${userId}?tab=zeiten` : `${ROOT_PATH}/admin/time-entries`;
    case "history":
      return userId ? `${baseCustomers}/${userId}?tab=historie` : baseCustomers;
    default:
      return null;
  }
};

type ResultState = "inactive" | "deleted" | null;

interface StatusMeta {
  state: ResultState;
  badge: string | null;
  detail: string | null;
}

const HIDDEN_STATUS_KEYS = new Set(["is_active", "deleted_at", "purge_at", "state"]);

const formatDate = (value: unknown): string | null => {
  if (value instanceof Date) {
    return value.toLocaleDateString("de-DE");
  }
  if (typeof value === "string" || typeof value === "number") {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString("de-DE");
    }
  }
  return null;
};

const deriveCustomerStatus = (item: SearchItem): StatusMeta => {
  if (item.entity !== "customer") {
    return { state: null, badge: null, detail: null };
  }
  const payload = (item.payload || {}) as Record<string, unknown>;
  const rawState = typeof payload["state"] === "string" ? (payload["state"] as string) : "";
  const deletedAt = payload["deleted_at"];
  const purgeAt = payload["purge_at"];
  const isActive = payload["is_active"];
  const hasDeletionFlag = Boolean(deletedAt);
  const isInactive = isActive === false && !hasDeletionFlag;
  const state: ResultState =
    rawState === "deleted"
      ? "deleted"
      : rawState === "inactive"
      ? "inactive"
      : hasDeletionFlag
      ? "deleted"
      : isInactive
      ? "inactive"
      : null;

  const deletedAtTxt = formatDate(deletedAt);
  const purgeAtTxt = formatDate(purgeAt);

  if (state === "deleted") {
    const parts = ["Zur Löschung vorgemerkt"];
    if (purgeAtTxt) {
      parts.push(`Entfernung ab ${purgeAtTxt}`);
    } else if (deletedAtTxt) {
      parts.push(`markiert am ${deletedAtTxt}`);
    }
    return { state, badge: "Löschung", detail: parts.join(" | ") };
  }
  if (state === "inactive") {
    return { state, badge: "Inaktiv", detail: deletedAtTxt ? `Inaktiv seit ${deletedAtTxt}` : "Konto ist inaktiv" };
  }
  return { state: null, badge: null, detail: null };
};

const SearchWindow: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [entities, setEntities] = useState<SearchEntityDescriptor[]>([]);
  const [entityKey, setEntityKey] = useState("customer");
  const entity = useMemo(() => entities.find((it) => it.key === entityKey), [entities, entityKey]);

  const [quickQuery, setQuickQuery] = useState("");
  const [quickField, setQuickField] = useState<string | null>(null);
  const [quickOperator, setQuickOperator] = useState<SearchOperator>("contains");

  const [advancedOpen, setAdvancedOpen] = useState(true);
  const [criteria, setCriteria] = useState<AdvancedCriterionInput[]>([]);

  const [results, setResults] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [savedSearches, setSavedSearches] = useState<SavedSearchSummary[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [buttonConfig, setButtonConfig] = useState<ButtonSlotConfig[]>([]);
  const [buttonEditorOpen, setButtonEditorOpen] = useState(false);

  const loadEntities = useCallback(async () => {
    const data = await api<SearchEntityDescriptor[]>("/api/search/entities");
    setEntities(data);
    if (data.length && !data.some((e) => e.key === entityKey)) {
      setEntityKey(data[0].key);
    }
  }, [entityKey]);

  const loadSavedSearches = useCallback(
    async (currentEntity: string) => {
      const data = await api<SavedSearchSummary[]>(`/api/search/saved?entity=${encodeURIComponent(currentEntity)}&include_global=1`);
      setSavedSearches(data);
    },
    []
  );

  const loadButtons = useCallback(async () => {
    const data = await api<ButtonConfigResponse>("/api/search/buttons");
    setButtonConfig(data.slots);
  }, []);

  const refreshData = useCallback(
    async (currentEntity: string) => {
      await Promise.all([loadSavedSearches(currentEntity), loadButtons()]);
    },
    [loadButtons, loadSavedSearches]
  );

  useEffect(() => {
    loadEntities().catch((err) => setError(err.message));
  }, [loadEntities]);

  useEffect(() => {
    if (entity) {
      const firstField = entity.default_quick_fields[0] ?? entity.fields[0]?.key ?? "";
      setQuickField(firstField || null);
      setCriteria([defaultAdvancedRow(firstField || entity.fields[0]?.key || "")]);
      refreshData(entity.key).catch((err) => setError(err.message));
    }
  }, [entity, refreshData]);

  const runSearch = useCallback(async () => {
    if (!entity) return;
    setLoading(true);
    setError(null);
    try {
      const payload = {
        entity: entity.key,
        quick: quickQuery
          ? {
              query: quickQuery,
              field: quickField,
              operator: quickOperator,
            }
          : null,
        criteria: criteria
          .filter((row) => row.value.trim().length > 0)
          .map((row) => ({
            field: row.field,
            operator: row.operator,
            value: row.value.trim(),
            conjunction: row.conjunction,
          })),
      };
      const data = await api<SearchResponse>("/api/search/execute", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setResults(data);
    } catch (err: any) {
      setError(err.message || "Suche fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, [criteria, entity, quickField, quickOperator, quickQuery]);

  const executeSaved = useCallback(
    async (savedId: number) => {
      setLoading(true);
      setError(null);
      try {
        const data = await api<SearchResponse>(`/api/search/execute_saved/${savedId}`, { method: "POST" });
        setEntityKey(data.entity);
        setResults(data);
      } catch (err: any) {
        setError(err.message || "Gespeicherte Suche konnte nicht geladen werden.");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const handleSaveSearch = useCallback(
    async (name: string, description: string, isGlobal: boolean) => {
      if (!entity) return;
      setSaving(true);
      try {
        await api<SavedSearchDetail>("/api/search/saved", {
          method: "POST",
          body: JSON.stringify({
            name,
            description,
            entity: entity.key,
            is_global: isGlobal,
            quick: quickQuery ? { query: quickQuery, field: quickField, operator: quickOperator } : null,
            criteria: criteria
              .filter((row) => row.value.trim().length > 0)
              .map((row) => ({
                field: row.field,
                operator: row.operator,
                value: row.value.trim(),
                conjunction: row.conjunction,
              })),
          }),
        });
        setSaveDialogOpen(false);
        await loadSavedSearches(entity.key);
      } catch (err: any) {
        setError(err.message || "Speichern fehlgeschlagen");
      } finally {
        setSaving(false);
      }
    },
    [criteria, entity, loadSavedSearches, quickField, quickOperator, quickQuery]
  );

  const handleDeleteSavedSearch = useCallback(
    async (searchId: number) => {
      const targetEntity = entity?.key || entityKey;
      if (!searchId || !targetEntity) return;
      if (!window.confirm("Gespeicherte Suche endgültig löschen?")) return;
      try {
        await api<void>(`/api/search/saved/${searchId}`, { method: "DELETE" });
        await refreshData(targetEntity);
      } catch (err: any) {
        setError(err.message || "Löschung fehlgeschlagen");
      }
    },
    [entity, entityKey, refreshData]
  );

  const handleButtonSave = useCallback(
    async (buttons: ButtonSlotConfig[]) => {
      try {
        const data = await api<ButtonConfigResponse>("/api/search/buttons", {
          method: "PUT",
          body: JSON.stringify({ buttons }),
        });
        setButtonConfig(data.slots);
        setButtonEditorOpen(false);
      } catch (err: any) {
        setError(err.message || "Buttons konnten nicht gespeichert werden.");
      }
    },
    []
  );

  const toggleWindow = useCallback(
    (state: boolean) => {
      setOpen(state);
      if (state && !results && quickQuery) {
        runSearch();
      }
    },
    [quickQuery, results, runSearch]
  );

  useEffect(() => {
    if (!(window as any).globalSearch) {
      (window as any).globalSearch = {
        open: () => toggleWindow(true),
        close: () => toggleWindow(false),
      };
    }
  }, [toggleWindow]);

  const addCriterion = () => {
    if (!entity) return;
    const firstField = entity.fields[0]?.key || "";
    setCriteria((prev) => [...prev, defaultAdvancedRow(firstField)]);
  };

  const updateCriterion = (id: string, patch: Partial<AdvancedCriterionInput>) => {
    setCriteria((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const removeCriterion = (id: string) => {
    setCriteria((prev) => (prev.length <= 1 ? prev : prev.filter((row) => row.id != id)));
  };

  return (
    <div className={`search-window ${open ? "is-open" : ""}`} aria-hidden={!open}>
      <div className="search-window__backdrop" onClick={() => toggleWindow(false)} />
      <div className="search-window__panel" role="dialog" aria-modal="true">
        <header className="search-window__head">
          <div>
            <h2>Globale Suche</h2>
            <p className="search-window__subtitle">{entity?.description || "Schnellzugriff über alle Entitäten"}</p>
          </div>
          <button className="btn btn-circle" onClick={() => toggleWindow(false)} aria-label="Fenster schließen">
            ×
          </button>
        </header>

        <section className="search-window__quick">
          <div className="search-row">
            <select value={entityKey} onChange={(ev) => setEntityKey(ev.target.value)}>
              {entities.map((ent) => (
                <option key={ent.key} value={ent.key}>
                  {ent.label}
                </option>
              ))}
            </select>
            <input
              type="text"
              value={quickQuery}
              onChange={(ev) => setQuickQuery(ev.target.value)}
              placeholder="Schnellsuche ..."
            />
            <button className="btn btn-primary" onClick={() => runSearch()} disabled={loading}>
              Suchen
            </button>
          </div>
          <div className="search-row search-row--secondary">
            <select value={quickField ?? ""} onChange={(ev) => setQuickField(ev.target.value || null)}>
              <option value="">Alle Felder</option>
              {entity?.fields
                .filter((field) => field.supports_quick)
                .map((field) => (
                  <option key={field.key} value={field.key}>
                    {field.label}
                  </option>
                ))}
            </select>
            <select value={quickOperator} onChange={(ev) => setQuickOperator(ev.target.value as SearchOperator)}>
              {operatorOptions.map((op) => (
                <option key={op.value} value={op.value}>
                  {op.label}
                </option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Suchbegriff"
              value={quickQuery}
              onChange={(ev) => setQuickQuery(ev.target.value)}
            />
            <button className="btn" onClick={() => setQuickQuery("")}>
              Zurücksetzen
            </button>
          </div>
        </section>

        <section className="search-window__buttons">
          <div className="button-bar">
            {Array.from({ length: MAX_BUTTON_SLOTS }).map((_, idx) => {
              const slot = idx + 1;
              const config = buttonConfig.find((it) => it.slot === slot);
              return (
                <button
                  key={slot}
                  className="btn"
                  disabled={!config}
                  onClick={() => config && executeSaved(config.saved_search_id)}
                  title={config?.label || `Button ${slot}`}
                >
                  {config?.label || `Slot ${slot}`}
                </button>
              );
            })}
            <button className="btn btn-ghost" onClick={() => setButtonEditorOpen(true)}>
              Buttons bearbeiten
            </button>
          </div>
        </section>

        <section className="search-window__advanced">
          <header>
            <h3>Erweiterte Suche</h3>
            <button className="link" onClick={() => setAdvancedOpen((prev) => !prev)}>
              {advancedOpen ? "Ausblenden" : "Einblenden"}
            </button>
          </header>
          {advancedOpen && (
            <div className="advanced-body">
              {criteria.map((row, index) => (
                <div className="advanced-row" key={row.id}>
                  <select value={row.field} onChange={(ev) => updateCriterion(row.id, { field: ev.target.value })}>
                    {entity?.fields.map((field) => (
                      <option key={field.key} value={field.key}>
                        {field.label}
                      </option>
                    ))}
                  </select>
                  <select value={row.operator} onChange={(ev) => updateCriterion(row.id, { operator: ev.target.value as SearchOperator })}>
                    {operatorOptions.map((op) => (
                      <option key={op.value} value={op.value}>
                        {op.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={row.value}
                    onChange={(ev) => updateCriterion(row.id, { value: ev.target.value })}
                    placeholder="Wert"
                  />
                  <select
                    value={row.conjunction}
                    onChange={(ev) => updateCriterion(row.id, { conjunction: ev.target.value as SearchConjunction })}
                    disabled={index === criteria.length - 1}
                  >
                    {conjunctionOptions.map((conj) => (
                      <option key={conj.value} value={conj.value}>
                        {conj.label}
                      </option>
                    ))}
                  </select>
                  <button className="btn btn-ghost" onClick={() => removeCriterion(row.id)} disabled={criteria.length === 1}>
                    –
                  </button>
                </div>
              ))}
              <button className="btn btn-secondary" onClick={addCriterion}>
                + Kriterium hinzufügen
              </button>
            </div>
          )}
        </section>

        <section className="search-window__actions">
          <div className="action-left">
            <button className="btn btn-primary" onClick={() => runSearch()} disabled={loading}>
              Suche ausführen
            </button>
            <button className="btn" onClick={() => setSaveDialogOpen(true)}>
              Suche speichern
            </button>
          </div>
          <div className="action-right">
            {loading && <span className="text-muted">Suche läuft ...</span>}
            {error && <span className="text-error">{error}</span>}
          </div>
        </section>

        <section className="search-window__results">
          {results ? (
            <Fragment>
              <div className="results-head">
                <strong>{results.total_count}</strong> Treffer für {entity?.label}
              </div>
              <div className="results-list">
                {results.items.map((item) => {
                  const link = buildResultLink(item);
                  const status = deriveCustomerStatus(item);
                  const filteredPayload = Object.entries(item.payload || {}).filter(([key]) =>
                    item.entity === "customer" ? !HIDDEN_STATUS_KEYS.has(key) : true
                  );
                  const cardClass = `result-card${status.state ? ` result-card--${status.state}` : ""}`;
                  const chipClass = status.state ? `result-chip result-chip--${status.state}` : "result-chip";
                  return (
                    <article key={`${item.entity}-${item.id}`} className={cardClass}>
                      <h4>
                        {link ? (
                          <a href={link} className="result-link">
                            {item.title}
                          </a>
                        ) : (
                          item.title
                        )}
                        {status.badge && <span className={chipClass}>{status.badge}</span>}
                      </h4>
                      {item.subtitle && <p>{item.subtitle}</p>}
                      <dl>
                        {status.detail && (
                          <div className="result-row result-row--status">
                            <dt>Inaktiv/Löschung</dt>
                            <dd>{status.detail}</dd>
                          </div>
                        )}
                        {filteredPayload.map(([key, value]) => (
                          <div key={key}>
                            <dt>{key}</dt>
                            <dd>{String(value ?? "")}</dd>
                          </div>
                        ))}
                      </dl>
                      {link && (
                        <p className="result-actions">
                          <a className="btn btn-secondary" href={link}>
                            Datensatz öffnen
                          </a>
                        </p>
                      )}
                    </article>
                  );
                })}
              </div>
            </Fragment>
          ) : (
            <p className="text-muted">Noch keine Ergebnisse – starte eine Suche oder wähle eine gespeicherte Abfrage.</p>
          )}
        </section>

        {saveDialogOpen && entity && (
          <SaveSearchDialog
            loading={saving}
            entity={entity}
            onClose={() => setSaveDialogOpen(false)}
            onSave={(name, description, isGlobal) => handleSaveSearch(name, description, isGlobal)}
          />
        )}

        {buttonEditorOpen && (
          <ButtonEditorDialog
            slots={buttonConfig}
            savedSearches={savedSearches}
            onCancel={() => setButtonEditorOpen(false)}
            onDeleteSavedSearch={handleDeleteSavedSearch}
            onSave={handleButtonSave}
          />
        )}
      </div>
    </div>
  );
};

const SaveSearchDialog: React.FC<{
  entity: SearchEntityDescriptor;
  loading: boolean;
  onSave: (name: string, description: string, isGlobal: boolean) => void;
  onClose: () => void;
}> = ({ entity, loading, onSave, onClose }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isGlobal, setIsGlobal] = useState(false);

  return (
    <div className="modal-overlay">
      <div className="modal-card">
        <h3>Suche speichern ({entity.label})</h3>
        <label>
          Bezeichnung
          <input type="text" value={name} onChange={(ev) => setName(ev.target.value)} />
        </label>
        <label>
          Beschreibung
          <input type="text" value={description} onChange={(ev) => setDescription(ev.target.value)} />
        </label>
        <label className="checkbox">
          <input type="checkbox" checked={isGlobal} onChange={(ev) => setIsGlobal(ev.target.checked)} />
          Global für alle Benutzer
        </label>
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button className="btn btn-primary" onClick={() => onSave(name, description, isGlobal)} disabled={loading}>
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
};

const ButtonEditorDialog: React.FC<{
  slots: ButtonSlotConfig[];
  savedSearches: SavedSearchSummary[];
  onSave: (buttons: ButtonSlotConfig[]) => void;
  onDeleteSavedSearch: (id: number) => void;
  onCancel: () => void;
}> = ({ slots, savedSearches, onSave, onDeleteSavedSearch, onCancel }) => {
  const buildSlots = useCallback(
    () =>
      Array.from({ length: MAX_BUTTON_SLOTS }).map((_, idx) => {
        const slot = idx + 1;
        const assign = slots.find((it) => it.slot === slot);
        return assign
          ? { ...assign }
          : {
              slot,
              saved_search_id: savedSearches[0]?.id ?? 0,
              label: "",
            };
      }),
    [savedSearches, slots]
  );

  const [localSlots, setLocalSlots] = useState<ButtonSlotConfig[]>(() => buildSlots());

  useEffect(() => {
    setLocalSlots(buildSlots());
  }, [buildSlots]);

  const updateSlot = (slot: number, patch: Partial<ButtonSlotConfig>) => {
    setLocalSlots((prev) => prev.map((row) => (row.slot === slot ? { ...row, ...patch } : row)));
  };

  return (
    <div className="modal-overlay">
      <div className="modal-card">
        <h3>Buttons konfigurieren</h3>
        <div className="button-editor">
          {localSlots.map((slot) => (
            <div key={slot.slot} className="button-editor__row">
              <span>Slot {slot.slot}</span>
              <select
                value={slot.saved_search_id}
                onChange={(ev) => updateSlot(slot.slot, { saved_search_id: Number(ev.target.value) })}
              >
                {savedSearches.map((search) => (
                  <option key={search.id} value={search.id}>
                    {search.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => onDeleteSavedSearch(slot.saved_search_id)}
                disabled={!slot.saved_search_id}
              >
                Löschen
              </button>
              <input
                type="text"
                placeholder="Label"
                value={slot.label ?? ""}
                onChange={(ev) => updateSlot(slot.slot, { label: ev.target.value })}
              />
            </div>
          ))}
        </div>
        <div className="modal-actions">
          <button className="btn" onClick={onCancel}>
            Abbrechen
          </button>
          <button className="btn btn-primary" onClick={() => onSave(localSlots)}>
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
};

function mountSearchApp() {
  const container = document.createElement("div");
  container.id = "global-search-root";
  document.body.appendChild(container);
  const root = createRoot(container);
  root.render(<SearchWindow />);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", mountSearchApp);
} else {
  mountSearchApp();
}
